// One step per thing the agent did, not per attempt it took to do it. Keep it import-free of React and of
// enso, so `node --test` can run stepChain.test.ts against it directly.
//
// The agent retries a lookup that came back empty, with different wording, and both attempts reach the panel
// as steps of their own: "Portal knowledge — No guidance on this" sits next to "Portal knowledge — Loaded",
// which reads as a failure the user has to interpret and is not what happened. The panel merges tool calls by
// `id`, so a retry that carries the id of the call it is retrying updates that step in place.

/** The part of an enso `AssistantStreamChunk` this needs. Restated structurally so this module imports nothing. */
export interface StepChunk {
  type: string;
  call?: { id: string; name: string; status?: string };
}

/**
 * Folds a tool call into the step before it when it repeats that step's tool immediately. Anything in between
 * (another tool, a word of the answer) means they are two things the agent did rather than one it had to try
 * twice, and both are shown.
 *
 * Stateful, and one is made per stream: the ids it rewrites are only meaningful within the turn that issued
 * them.
 */
export const foldRetriedSteps = (): ((chunk: StepChunk) => StepChunk) => {
  const foldedInto = new Map<string, string>();
  let previous: { name: string; id: string } | null = null;

  return chunk => {
    if (chunk.type !== 'tool-call' || chunk.call === undefined) {
      // A word of the answer between two calls means the second is not a retry of the first.
      if (chunk.type === 'text-delta') previous = null;
      return chunk;
    }

    const { id, name, status } = chunk.call;

    const already = foldedInto.get(id);
    if (already !== undefined) return { ...chunk, call: { ...chunk.call, id: already } };

    if (status === 'running' && previous !== null && previous.name === name) {
      foldedInto.set(id, previous.id);
      return { ...chunk, call: { ...chunk.call, id: previous.id } };
    }

    previous = { name, id };
    return chunk;
  };
};
